import React, { useRef, useEffect, useState } from "react";
import {
  LuCircleDollarSign,
  LuWallet,
  LuSettings,
  LuArrowUp,
  LuArrowDown,
  LuShieldCheck,
  LuZap
} from "react-icons/lu";
import { FiCheckCircle } from "react-icons/fi";
import { motion, useScroll, useTransform, animate, useInView } from "framer-motion";

function CountUpAmount({ value }) {
  const nodeRef = useRef(null);
  const isInView = useInView(nodeRef, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView && nodeRef.current) {
      const controls = animate(0, value, {
        duration: 2,
        ease: "easeOut",
        onUpdate(v) {
          if (nodeRef.current) {
            nodeRef.current.textContent = "$" + Math.round(v).toLocaleString('en-IN');
          }
        }
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return (
    <h3 ref={nodeRef} className="text-[32px] font-sans font-bold text-[#1C2035] leading-[48px] mb-3 tracking-tight">
      $0
    </h3>
  );
}

export default function Transactions() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const progressHeight = useTransform(scrollYProgress, [0, 0.25, 0.5, 0.75], ["0%", "33.333%", "66.666%", "100%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-75%"]);

  const colorInactive = "#E5E7EB";
  const colorActive = "#0D211D";

  const dot2Bg = useTransform(scrollYProgress, [0.22, 0.25], [colorInactive, colorActive]);
  const dot3Bg = useTransform(scrollYProgress, [0.47, 0.5], [colorInactive, colorActive]);
  const dot4Bg = useTransform(scrollYProgress, [0.72, 0.75], [colorInactive, colorActive]);

  const step1Opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.2]);
  const step2Opacity = useTransform(scrollYProgress, [0.15, 0.25, 0.45, 0.55], [0.2, 1, 1, 0.2]);
  const step3Opacity = useTransform(scrollYProgress, [0.4, 0.5, 0.7, 0.8], [0.2, 1, 1, 0.2]);
  const step4Opacity = useTransform(scrollYProgress, [0.65, 0.75, 1], [0.2, 1, 1]);

  return (
    <section ref={containerRef} className="w-full relative h-[400vh]">
      <div className="sticky top-0 h-screen w-full flex items-center overflow-hidden pt-[112px] pb-[112px]">
        <div className="w-full max-w-[1240px] mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center">
          
          <div className="flex overflow-hidden h-[450px] lg:h-[560px]">
            <div className="relative w-[32px] flex-shrink-0 hidden md:flex flex-col items-center py-[20px] justify-between h-full">
              <div className="absolute left-[15px] top-[20px] bottom-[20px] w-[2px] bg-[#E5E7EB] overflow-hidden z-0">
                <motion.div className="w-full bg-[#0D211D] origin-top" style={{ height: progressHeight }} />
              </div>
              <motion.div className="w-4 h-4 rounded-full ring-[5px] ring-white z-10 box-content" style={{ backgroundColor: colorActive }}></motion.div>
              <motion.div className="w-4 h-4 rounded-full ring-[5px] ring-white z-10 box-content" style={{ backgroundColor: dot2Bg }}></motion.div>
              <motion.div className="w-4 h-4 rounded-full ring-[5px] ring-white z-10 box-content" style={{ backgroundColor: dot3Bg }}></motion.div>
              <motion.div className="w-4 h-4 rounded-full ring-[5px] ring-white z-10 box-content" style={{ backgroundColor: dot4Bg }}></motion.div>
            </div>
            
            <div className="flex-1 relative h-full md:pl-10 overflow-hidden">
              <motion.div className="w-full flex flex-col gap-[48px]" style={{ y: contentY }}>
                <motion.div className="w-full flex flex-col justify-center" style={{ opacity: step1Opacity }}>
                  <div className="w-[64px] h-[64px] rounded-[16px] border border-gray-100 bg-[#F8F9FA] flex items-center justify-center mb-6 shadow-sm">
                    <LuCircleDollarSign className="w-7 h-7 text-[#073634]" strokeWidth={2} />
                  </div>
                  <h2 className="text-[36px] md:text-[44px] font-sans font-bold text-[#073634] mb-4 leading-[1.1] tracking-tight">
                    Instant Transactions
                  </h2>
                  <p className="text-[17px] text-gray-500 leading-[1.6] mb-8 max-w-[440px]">
                    Process payments in real-time with ultra-fast confirmation speeds. No waiting, no delays your money moves at the speed of business.
                  </p>
                </motion.div>
                
                <motion.div className="w-full flex flex-col justify-center" style={{ opacity: step2Opacity }}>
                  <div className="w-[64px] h-[64px] rounded-[16px] border border-gray-100 bg-[#F8F9FA] flex items-center justify-center mb-6 shadow-sm">
                    <LuWallet className="w-7 h-7 text-[#073634]" strokeWidth={2} />
                  </div>
                  <h2 className="text-[36px] md:text-[44px] font-sans font-bold text-[#073634] mb-4 leading-[1.1] tracking-tight">
                    Global Payment Support
                  </h2>
                  <p className="text-[17px] text-gray-500 leading-[1.6] mb-8 max-w-[440px]">
                    Accept and send payments across 150+ countries with multi-currency support. Expand globally without payment limitations holding you back.
                  </p>
                </motion.div>

                <motion.div className="w-full flex flex-col justify-center" style={{ opacity: step3Opacity }}>
                  <div className="w-[64px] h-[64px] rounded-[16px] border border-gray-100 bg-[#F8F9FA] flex items-center justify-center mb-6 shadow-sm">
                    <LuShieldCheck className="w-7 h-7 text-[#073634]" strokeWidth={2} />
                  </div>
                  <h2 className="text-[36px] md:text-[44px] font-sans font-bold text-[#073634] mb-4 leading-[1.1] tracking-tight">
                    Zero Hidden Fees
                  </h2>
                  <p className="text-[17px] text-gray-500 leading-[1.6] mb-8 max-w-[440px]">
                    Experience completely transparent pricing. You only pay for what you use with no unexpected charges or complicated fee structures holding you back.
                  </p>
                </motion.div>

                <motion.div className="w-full flex flex-col justify-center" style={{ opacity: step4Opacity }}>
                  <div className="w-[64px] h-[64px] rounded-[16px] border border-gray-100 bg-[#F8F9FA] flex items-center justify-center mb-6 shadow-sm">
                    <LuZap className="w-7 h-7 text-[#073634]" strokeWidth={2} />
                  </div>
                  <h2 className="text-[36px] md:text-[44px] font-sans font-bold text-[#073634] mb-4 leading-[1.1] tracking-tight">
                    Lightning Fast API
                  </h2>
                  <p className="text-[17px] text-gray-500 leading-[1.6] mb-8 max-w-[440px]">
                    Integrate our seamless payment infrastructure into your product in minutes. Our developer-friendly API ensures a smooth setup process.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
          
          <div className="w-full relative flex items-center justify-center lg:justify-end">
            <div className="relative w-full max-w-[560px]">
              <img src="https://cdn.jiro.build/Random/Image%20sectin.png" alt="Transaction dashboard with users" className="w-full h-auto object-contain rounded-[32px] drop-shadow-sm" />
              
              <img src="https://cdn.jiro.build/Random/dolar%20note.png" alt="Dollar bill" className="absolute -left-[100px] top-[45%] lg:-left-[140px] lg:top-[50%] -translate-y-1/2 w-[140px] lg:w-[180px] rotate-[-22deg] drop-shadow-xl z-20 hidden md:block pl-[14px]" />
              
              <motion.div 
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                className="absolute -bottom-8 -right-4 md:-right-8 lg:-bottom-12 lg:-right-12 w-[360px] bg-white rounded-3xl p-6 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] z-30 ml-0 mt-0 mb-[80px] mr-[140px] h-[312.5px]"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-2">
                    <LuWallet className="w-6 h-6 text-[#073634]" fill="#073634" />
                    <span className="font-bold text-[#073634] text-[20px]">My Balance</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 cursor-pointer hover:text-gray-700 transition-colors">
                    <span className="text-[14px]">More Option</span>
                    <LuSettings className="w-4 h-4" />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-gray-600 text-[16px]">US Dolar</span>
                  <span className="text-2xl leading-none">🇺🇸</span>
                </div>
                
                <CountUpAmount value={6348100} />
                
                <div className="flex items-center gap-1.5 mb-8">
                  <FiCheckCircle className="w-5 h-5 text-[#00A859] fill-[#00A859]" stroke="white" strokeWidth={2} />
                  <span className="text-[#00A859] text-[15px] font-medium">15.43% Than last month</span>
                </div>
                
                <div className="flex items-center gap-3 w-full">
                  <button className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#F4F5F7] hover:bg-[#E5E7EB] transition-colors rounded-[16px] text-[#111] font-sans font-medium text-[16px]">
                    <LuArrowUp className="w-4 h-4" /> Send
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#F4F5F7] hover:bg-[#E5E7EB] transition-colors rounded-[16px] text-[#111] font-sans font-medium text-[16px]">
                    <LuArrowDown className="w-4 h-4" /> Request
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}